import {Component, inject, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {filter, map, mergeMap, startWith, take, tap} from 'rxjs/operators';
import {AutoUnsubscribeTakeUntilClass} from 'shared/models';
import {DataEntity} from 'octopus-connect';
import {combineLatest, of} from 'rxjs';
import {MatDialog} from '@angular/material/dialog';
import {CompassService} from '../../../compass.service';
import {CompassExploreAttributes, CompassThemeEntity, CompassThemeResourceGroup} from '../../../types';
import {WarningComponent} from '../../../ui';

interface ResourceGroupViewModel {
    title: string
    resources: DataEntity[]
}

@Component({
    selector: 'app-compass-resources',
    templateUrl: './compass-resources.component.html',
    standalone: false
})
export class CompassResourcesComponent extends AutoUnsubscribeTakeUntilClass implements OnInit {
    public activatedRoute = inject(ActivatedRoute);
    public dialog = inject(MatDialog);
    private compassService = inject(CompassService);
    private router = inject(Router);

    private exploreItem$ = this.activatedRoute.params.pipe(
        filter(params => params.exploreId),
        mergeMap(params => this.compassService.loadExploreEntity(params.exploreId)),
        startWith(null), // on peut arriver sur la page sans exploreId
    );
    private theme$ = this.activatedRoute.params.pipe(
        filter(params => params.themeId),
        mergeMap(params => this.compassService.loadThemeById(params.themeId)),
    );

    public parentItems$ = combineLatest([this.exploreItem$, this.theme$]).pipe(
        map(([exploreItem, theme]) => ({exploreItem, theme})),
    );


    public displayLoader = true;
    public resourceGroups: ResourceGroupViewModel[] = [];
    public goBackLinkName = 'compass.home';
    public selectedThemeTitle = '';

    /****** Lifecycle Hooks ******/

    ngOnInit() {
        this.theme$.pipe(
            take(1),
            tap(theme => this.selectedThemeTitle = theme.get('label')),
            tap(theme => this.initialize(theme))
        ).subscribe();

        this.activatedRoute.queryParams.subscribe(params => {
            this.goBackLinkName = params.backLinkName ?? 'compass.home';
        });
    }

    /****** Logic ******/

    public getResourceType(resource: DataEntity, useSlug?: boolean): string {
        if (resource.get('type') && resource.get('type')[0] && resource.get('type')[0]['name']) {
            if (useSlug) {
                return this.slugify(resource.get('type')[0]['name']);
            }
            return resource.get('type')[0]['name'];
        }
        return '';
    }

    public isDialogResource(resource: DataEntity) {
        return resource.get('link') && resource.get('link').length > 0;
    }

    private initialize(theme: CompassThemeEntity): void {
        const groups = this.getDisplayGroups(theme);
        const resourceIds = groups.flatMap(group => group.resources);

        const resources$ = resourceIds.length
            ? this.compassService.loadResources({filter: {id: resourceIds}}).pipe(take(1))
            : of([]);

        combineLatest([resources$]).pipe(
            take(1),
            tap(([resources]) => this.resourceGroups = this.mapResourceGroups(groups, resources)),
            tap(() => this.displayLoader = false)
        ).subscribe();
    }

    private getDisplayGroups(theme: CompassThemeEntity): CompassThemeResourceGroup[] {
        const resourceGroups = theme.get('resource_groups') ?? [];

        if (resourceGroups.length > 0) {
            return resourceGroups;
        }

        return [{
            title: '',
            resources: theme.get('ressources') ?? []
        }];
    }

    private mapResourceGroups(groups: CompassThemeResourceGroup[], resources: DataEntity[]): ResourceGroupViewModel[] {
        const resourceMap = new Map(resources.map(resource => [String(resource.id), resource]));

        return groups.map(group => ({
            title: group.title,
            resources: group.resources
                .map(resourceId => resourceMap.get(String(resourceId)))
                .filter(resource => !!resource)
        }));
    }

    // slug function convert to lowercase and replace space with - and no special characters

    private slugify(text: string): string {
        const dict = {
            'à': 'a',
            'á': 'a',
            'é': 'e',
            'è': 'e',
            'ê': 'e',
            'ç': 'c',
            'ë': 'e',
            'ï': 'i',
            'î': 'i',
            'ô': 'o',
            'ö': 'o',
            'ù': 'u',
            'û': 'u',
            'ü': 'u',
            'ÿ': 'y',
            'ñ': 'n'
        };
        return text.toString().toLowerCase()
            .replace(/\s+/g, '_')           // Replace spaces with -
            .replace(/[^\w ]/g, function (char) {
                return dict[char] || char;
            })
            .replace(/[^\w\-]+/g, '')       // Remove all non-word chars
            .replace(/\_\_+/g, '_')         // Replace multiple - with single -
            .replace(/^_+/, '')             // Trim - from start of text
            .replace(/_+$/, '');            // Trim - from end of text
    }

    public getQueryParams() {
        const qp = {
            fromUrl: this.router.url.split('?')[0],
            selectedThemeTitle: this.selectedThemeTitle,
        };

        if (Object.keys(this.activatedRoute.snapshot.queryParams).length) {
            qp['fromQueryParams'] = JSON.stringify(this.activatedRoute.snapshot.queryParams);
        }

        return qp;
    }

    public getBackQueryParams() {
        return this.activatedRoute.snapshot.queryParams.fromQueryParams ? JSON.parse(this.activatedRoute.snapshot.queryParams.fromQueryParams) : null;
    }

    public getBackUrl() {
        return this.activatedRoute.snapshot.queryParams.fromUrl ?? '../..';
    }

    /***** Events handling *****/

    public onSeeResourceClick(resource: DataEntity) {
        if (resource.get('link').includes('http')) {
            const dialogRef = this.dialog.open(WarningComponent);
            dialogRef.afterClosed().subscribe(result => {
                if (result) {
                    window.open(resource.get('link'), '_blank');
                }
            });
        } else {
            this.router.navigateByUrl(resource.get('link'));
        }
    }

    public getMediaUrl(medias: CompassExploreAttributes['medias']): string {
        return medias && medias.length ? medias[0].url : '';
    }
}
